/**
 * ⚠️ BULLETPROOF AUTH SYSTEM - DO NOT MODIFY ⚠️
 * 
 * This file is part of the immutable auth system.
 * Any modifications will break the login flow.
 * 
 * If your feature needs auth changes, adapt your feature instead.
 * Authentication guard component
 */

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useAuth } from '../../stores/authStore';

/**
 * BULLETPROOF AUTH GUARDS
 * 
 * RULES:
 * 1. NO loading UI - causes complexity and stuck states
 * 2. NO error handling - let routes handle their own errors
 * 3. NO toast notifications - they break the flow
 * 4. SIMPLE redirect logic only
 * 5. NEVER modify these guards for feature requests
 */

interface AuthGuardProps {
  children: React.ReactNode;
}

// Protected Route - requires authentication
export const ProtectedRoute: React.FC<AuthGuardProps> = ({ children }) => {
  const { isAuthenticated, isInitialized } = useAuth();
  
  // Show loading indicator while initializing
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect to login if not authenticated, save current path for redirect after login
  if (!isAuthenticated) {
    const currentPath = window.location.pathname + window.location.search;
    return <Navigate to={`/login?redirect=${encodeURIComponent(currentPath)}`} replace />;
  }

  return <>{children}</>;
};

// Guest Only Route - redirects authenticated users
export const GuestOnlyRoute: React.FC<AuthGuardProps> = ({ children }) => {
  const { isAuthenticated, isInitialized } = useAuth();

  // Show loading indicator while initializing
  if (!isInitialized) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-sm">Loading...</p>
        </div>
      </div>
    );
  }

  // Redirect authenticated users if trying to access login page
  if (isAuthenticated && window.location.pathname === '/login') {
    // Check if there's a redirect parameter
    const params = new URLSearchParams(window.location.search);
    const redirectTo = params.get('redirect') || '/my-account';
    return <Navigate to={redirectTo} replace />;
  }

  return <>{children}</>;
};

// Public Route - accessible to everyone (no guards)
export const PublicRoute: React.FC<AuthGuardProps> = ({ children }) => {
  return <>{children}</>;
};

// Export default ProtectedRoute for backward compatibility
export default ProtectedRoute;